import React, { Component } from 'react'
import type { ReactNode, RefObject } from 'react'
import Overlay, { DrawerOverlayProps } from './DrawerOverlay'
import { computePosition, detectOverflow } from '@floating-ui/dom'
import type { ComputePositionReturn, MiddlewareArguments, Placement } from '@floating-ui/core'
import styles from './drawer.module.css'

export interface DrawerProps extends Pick<DrawerOverlayProps, 'setOpen'|'isModal'|'closeOnOverlayClick'>{
	isOpen: boolean
  animateInitialOpen?: boolean
  showCloseButton?: boolean
	placement?: Placement
	width?: string
	height?: string
}

type DrawerState = Omit<ComputePositionReturn, 'x' | 'y'> & {
  x: number | null
  y: number | null
	renderChildren: boolean
}

const flipInner = {
	name: 'flipInner',
	async fn(args: MiddlewareArguments) {
		let { x, y } = args
		const { top, bottom, left, right } = await detectOverflow(args)
		if (top > 0) {
			y += top
		}
		if (bottom > 0) {
			y -= bottom 
		}
		if (left > 0) {
			x += left
		}
		if (right > 0) {
			x -= right
		}
		return { x, y }
	}
}


export default class Drawer extends Component<DrawerProps, DrawerState>{
	overlayRef: RefObject<HTMLElement> = null
	contentRef: RefObject<HTMLElement> = null
	rendered = false
	animating = false
	skipInner = false
	closeTimeout = null
	state: DrawerState = {
		x: 0,
		y: 0,
		placement: 'bottom',
		strategy: 'absolute',
		middlewareData: {},
		renderChildren: false
	}

	constructor(props: DrawerProps) {
		super(props)
		this.overlayRef = React.createRef()
		this.contentRef = React.createRef()
		if (props.isOpen) {
			this.state.renderChildren = props.isOpen
			this.skipInner = !!props.animateInitialOpen
		} else {
			this.skipInner = true
		}
	}

  setOpen = (isOpen: boolean) => {
    if (this.animating && !isOpen) {
      this.closeTimeout = setTimeout(() => {
        this.setState({ renderChildren: false })
        if (this.props.setOpen) {
          this.props.setOpen(isOpen)
        }
        this.closeTimeout = null
      }, 200)
      this.updatePosition(false)
    } else {
      requestAnimationFrame(this.updatePosition)
      if (this.closeTimeout) {
        clearTimeout(this.closeTimeout)
        this.closeTimeout = null
      }
      this.setState({ renderChildren: isOpen })
      if (this.props.setOpen) {
        this.props.setOpen(isOpen)
      }
    }
  }

	updatePosition = (forceOpen?: any) => {
		const { placement = 'bottom', isOpen } = this.props
    const doFlip = typeof forceOpen === 'boolean' ? forceOpen : isOpen && !this.skipInner
		computePosition(this.overlayRef.current, this.contentRef.current, {
			placement,
			strategy: 'absolute',
			middleware: doFlip ? [flipInner] : [],
		}).then(pos => {
			if (this.skipInner) {
				this.skipInner = false
				requestAnimationFrame(() => {
					this.animating = true
					this.updatePosition()
				})
			}
			if (!this.animating) {
				requestAnimationFrame(() => {
          this.animating = true;
        })
			}
			this?.setState(pos)
		})
	}

	componentDidMount(){
		window.addEventListener('resize', this.updatePosition)
	}

	componentDidUpdate(prevProps: DrawerProps) {
		const { isOpen, placement } = this.props
		if (prevProps.placement !== placement) {
			this.animating = false
			this.rendered = false
			if (!isOpen) {
				this.skipInner = true
			}
			if (isOpen && prevProps.isOpen) {
				this.updatePosition()
			}
		}
		if (prevProps.isOpen !== isOpen) {
      this.setOpen(isOpen)
		}
	}
	componentWillUnmount() {
		window.removeEventListener('resize', this.updatePosition)
	}

	render() {
		const { children, isModal, isOpen, showCloseButton, closeOnOverlayClick } = this.props
		const { x, y, renderChildren } = this.state
		const clsInvisible = !this.rendered ? styles.drawerInvisible : ''
		if (!this.rendered && renderChildren) {
			this.rendered = true
      requestAnimationFrame(this.updatePosition)
		}
		const clsAnimate = this.animating ? styles.drawerAnimate : ''
		return (
			<Overlay
        overlayRef={this.overlayRef}
        isModal={isModal}
        isOpen={renderChildren}
        setOpen={this.setOpen}
        closeOnOverlayClick={closeOnOverlayClick}
      >
				<div
				  className={`${styles.drawer} ${clsInvisible} ${clsAnimate}`}
				  ref={this.contentRef as RefObject<HTMLDivElement>} style={{
						transform: `translate(${Math.round(x)}px,${Math.round(y)}px)`,
						width: this.props.width || null,
						height: this.props.height || null,
					}}
				>
          {!renderChildren ? null : <>
            {showCloseButton && <button className={styles.closeButton} onClick={() => this.setOpen(!isOpen)}>&#215;</button>}
            {children}
          </>}
				</div>
			</Overlay>
		)
	}
}